import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteField,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  limit,
  Timestamp,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAccountAccessError } from '../utils/access';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseEnabled = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId &&
  firebaseConfig.appId,
);

const app = firebaseEnabled && !getApps().length ? initializeApp(firebaseConfig) : getApps()[0];

export const auth = firebaseEnabled ? getAuth(app) : null;
export const db = firebaseEnabled ? getFirestore(app) : null;
export const functions = firebaseEnabled ? getFunctions(app) : null;

const snapshotToArray = (snapshot) => snapshot.docs.map((item) => ({ docId: item.id, ...item.data() }));

const toIso = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  return value;
};

const normalizeUser = (user) => ({
  ...user,
  id: user.id || user.uid || user.docId,
  uid: user.uid || user.docId,
  stars: user.stars || 0,
});

const normalizeSubmission = (submission) => ({
  ...submission,
  docId: submission.docId,
  submittedAt: toIso(submission.submittedAt),
  resubmittedAt: toIso(submission.resubmittedAt),
  reviewedAt: toIso(submission.reviewedAt),
});

const normalizeNotification = (notification) => ({
  ...notification,
  id: notification.docId || notification.id,
  createdAt: toIso(notification.createdAt),
  readAt: toIso(notification.readAt),
  resolvedAt: toIso(notification.resolvedAt),
});

const normalizeQuestionnaireDraft = (draft) => ({
  ...draft,
  updatedAt: toIso(draft.updatedAt),
});

export const listenToAuth = (callback) => {
  if (!firebaseEnabled) return () => {};
  return onAuthStateChanged(auth, callback);
};

export const loginWithEmail = async (email, password) => {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  const profile = await getUserProfile(credential.user.uid);
  const accessError = getAccountAccessError(profile);
  if (accessError) {
    await signOut(auth);
    throw new Error(accessError);
  }
  await recordSessionActivity('login');
  return profile;
};

export const logoutFirebase = async () => {
  if (!firebaseEnabled) return;
  if (auth.currentUser) await recordSessionActivity('logout');
  await signOut(auth);
};

const recordSessionActivity = async (action) => {
  try { await httpsCallable(functions, 'recordSessionActivity')({ action }); }
  catch (error) { console.error('No se pudo registrar la sesión:', error.code); }
};

export const getUserProfile = async (uid) => {
  if (!firebaseEnabled || !uid) return null;
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return normalizeUser({ docId: snap.id, ...snap.data() });
};

export const subscribeBarometroData = ({ role = 'public', uid = '', onData, onError }) => {
  if (!firebaseEnabled) return () => {};

  const state = {
    users: [],
    companyProfileLoaded: false,
    submissions: [],
    publishedHistory: {},
    deadlines: {},
    deadlineExtensions: {},
    notifications: [],
  };

  const emit = () => onData({ ...state });
  const subscriptions = [];

  subscriptions.push(
    onSnapshot(
      collection(db, 'publishedHistory'),
      (snapshot) => {
        state.publishedHistory = snapshot.docs.reduce((acc, item) => {
          acc[item.id] = item.data();
          return acc;
        }, {});
        emit();
      },
      onError,
    ),
  );

  if (role === 'admin') {
    subscriptions.push(
      onSnapshot(
        collection(db, 'users'),
        (snapshot) => {
          state.users = snapshotToArray(snapshot).map(normalizeUser);
          emit();
        },
        onError,
      ),
      onSnapshot(
        collection(db, 'submissions'),
        (snapshot) => {
          state.submissions = snapshotToArray(snapshot).map(normalizeSubmission);
          emit();
        },
        onError,
      ),
      onSnapshot(
        query(collection(db, 'adminNotifications'), orderBy('createdAt', 'desc')),
        (snapshot) => {
          state.notifications = snapshotToArray(snapshot).map(normalizeNotification);
          emit();
        },
        onError,
      ),
    );
  }

  if (role === 'company' && uid) {
    subscriptions.push(
      onSnapshot(
        doc(db, 'users', uid),
        (snapshot) => {
          state.users = snapshot.exists() ? [normalizeUser({ docId: snapshot.id, ...snapshot.data() })] : [];
          state.companyProfileLoaded = true;
          emit();
        },
        onError,
      ),
      onSnapshot(
        query(collection(db, 'submissions'), where('companyUid', '==', uid)),
        (snapshot) => {
          state.submissions = snapshotToArray(snapshot).map(normalizeSubmission);
          emit();
        },
        onError,
      ),
    );
  }

  if (role === 'admin' || role === 'company') {
    subscriptions.push(
      onSnapshot(
      collection(db, 'deadlines'),
      (snapshot) => {
        state.deadlines = snapshot.docs.reduce((acc, item) => {
          acc[item.id] = item.data()?.date || '';
          return acc;
        }, {});
        emit();
      },
      onError,
      ),
      onSnapshot(
        collection(db, 'deadlineExtensions'),
        (snapshot) => {
          state.deadlineExtensions = snapshot.docs.reduce((acc, item) => {
            acc[item.id] = item.data()?.extensions || {};
            return acc;
          }, {});
          emit();
        },
        onError,
      ),
    );
  }

  return () => subscriptions.forEach((unsubscribe) => unsubscribe());
};

export const createCompanyUser = async (payload) => {
  const callable = httpsCallable(functions, 'createCompanyUser');
  const result = await callable(payload);
  return result.data || null;
};

export const registerCompany = async (payload) => {
  if (!firebaseEnabled) throw new Error('El registro requiere conexión al servidor.');
  const result = await httpsCallable(functions, 'registerCompany')(payload);
  return result.data;
};

export const reviewCompanyRegistration = async (uid, decision) => {
  const result = await httpsCallable(functions, 'reviewCompanyRegistration')({ uid, decision });
  return result.data;
};

export const updateCompanyUser = async (payload) => {
  const callable = httpsCallable(functions, 'updateCompanyUser');
  const result = await callable(payload);
  return result.data || null;
};

export const deleteCompanyUser = async (uid) => {
  const callable = httpsCallable(functions, 'deleteCompanyUser');
  const result = await callable({ uid });
  return result.data || null;
};

export const sendCompanyReminder = async ({ uid, period }) => {
  const callable = httpsCallable(functions, 'sendCompanyReminder');
  const result = await callable({ uid, period });
  return result.data || null;
};

export const completeInitialPasswordChange = async (newPassword) => {
  const callable = httpsCallable(functions, 'completeInitialPasswordChange');
  const result = await callable({ newPassword });
  return result.data || null;
};

export const testTransactionalEmail = async (email) => {
  const callable = httpsCallable(functions, 'testTransactionalEmail');
  const result = await callable({ email });
  return result.data || null;
};

export const generateReportNarrative = async (report) => {
  const callable = httpsCallable(functions, 'generateReportNarrative');
  const result = await callable({ report });
  return result.data || null;
};

export const acceptLegalTerms = async ({ version, termsVersion, privacyVersion, userAgent }) => {
  const callable = httpsCallable(functions, 'acceptLegalTerms');
  const result = await callable({ version, termsVersion, privacyVersion, userAgent });
  return result.data?.legalAcceptance || null;
};

export const saveUserProfile = (user) => (
  setDoc(doc(db, 'users', user.uid || user.docId), { ...user, updatedAt: serverTimestamp() }, { merge: true })
);

export const saveDeadline = (month, date) => (
  setDoc(doc(db, 'deadlines', month), { date, updatedAt: serverTimestamp() }, { merge: true })
);

export const saveDeadlineExtension = (month, companyId, date, existingExtensions = {}) => (
  setDoc(
    doc(db, 'deadlineExtensions', month),
    {
      extensions: { ...existingExtensions, [companyId]: date },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
);

export const removeDeadlineExtension = (month, companyId, existingExtensions = {}) => {
  const nextExtensions = { ...existingExtensions };
  delete nextExtensions[companyId];
  return setDoc(
    doc(db, 'deadlineExtensions', month),
    {
      extensions: nextExtensions,
      updatedAt: serverTimestamp(),
    },
  );
};

export const saveSubmission = (submission) => (
  setDoc(doc(db, 'submissions', submission.docId), { ...submission, updatedAt: serverTimestamp() }, { merge: true })
);

export const updateSubmission = (docId, patch) => (
  updateDoc(doc(db, 'submissions', docId), { ...patch, updatedAt: serverTimestamp() })
);

export const deleteSubmission = (docId) => (
  firebaseEnabled && docId
    ? deleteDoc(doc(db, 'submissions', docId))
    : Promise.resolve()
);

export const getQuestionnaireDraft = async (draftId) => {
  if (!firebaseEnabled || !draftId) return null;
  const snap = await getDoc(doc(db, 'questionnaireDrafts', draftId));
  if (!snap.exists()) return null;
  return normalizeQuestionnaireDraft({ docId: snap.id, ...snap.data() });
};

export const saveQuestionnaireDraft = (draft) => (
  setDoc(
    doc(db, 'questionnaireDrafts', draft.docId),
    {
      ...draft,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
);

export const deleteQuestionnaireDraft = (draftId) => (
  firebaseEnabled && draftId
    ? deleteDoc(doc(db, 'questionnaireDrafts', draftId))
    : Promise.resolve()
);

export const savePublishedEntry = (month, entry) => (
  setDoc(doc(db, 'publishedHistory', month), { ...entry, publishedAt: serverTimestamp() }, { merge: true })
);

export const savePublishedArchiveEntry = (month, entry) => (
  addDoc(collection(db, 'publicationArchive'), {
    period: month,
    ...entry,
    archivedAt: serverTimestamp(),
  })
);

export const savePublishedResultWithArchive = (month, publicEntry, archiveEntry) => {
  const batch = writeBatch(db);
  const publishedRef = doc(db, 'publishedHistory', month);
  const archiveRef = doc(collection(db, 'publicationArchive'));

  batch.set(publishedRef, {
    ...publicEntry,
    publishedAt: serverTimestamp(),
    aiNarrative: deleteField(),
    aiNarrativeGeneratedAt: deleteField(),
    aiNarrativeGeneratedBy: deleteField(),
    aiNarrativeModel: deleteField(),
    aiNarrativeSource: deleteField(),
    aiCleanedVerbatims: deleteField(),
  }, { merge: true });
  batch.set(archiveRef, {
    period: month,
    ...archiveEntry,
    archivedAt: serverTimestamp(),
  });

  return batch.commit();
};

export const createAdminNotification = (notification) => (
  addDoc(collection(db, 'adminNotifications'), {
    ...notification,
    createdAt: serverTimestamp(),
    status: notification.status || 'unread',
  })
);

export const updateAdminNotification = (notificationId, patch) => (
  updateDoc(doc(db, 'adminNotifications', notificationId), { ...patch, updatedAt: serverTimestamp() })
);

export const markAllAdminNotificationsRead = async () => {
  const snapshot = await getDocs(collection(db, 'adminNotifications'));
  const updates = snapshot.docs
    .filter((item) => item.data()?.status === 'unread')
    .map((item) => updateDoc(item.ref, { status: 'read', readAt: serverTimestamp(), updatedAt: serverTimestamp() }));
  await Promise.all(updates);
};


export const resendCompanyAccess = async (uid) => {
  if (!firebaseEnabled) throw new Error('El reenvío requiere conexión a Firebase.');
  const result = await httpsCallable(functions, 'resendCompanyAccess')({ uid });
  return result.data;
};

export const subscribeOperationalLogs = ({ kind, count = 100, from = '', to = '', onData, onError }) => {
  if (!firebaseEnabled) { onData([]); return () => {}; }
  const constraints = [orderBy('createdAt', 'desc')];
  if (from) constraints.push(where('createdAt', '>=', Timestamp.fromDate(new Date(`${from}T00:00:00-05:00`))));
  if (to) constraints.push(where('createdAt', '<=', Timestamp.fromDate(new Date(`${to}T23:59:59.999-05:00`))));
  constraints.push(limit(count));
  return onSnapshot(query(collection(db, kind === 'email' ? 'emailLogs' : 'auditLogs'), ...constraints), snapshot => {
    onData(snapshot.docs.map(item => ({ ...item.data(), docId: item.id,
      createdAt: toIso(item.data().createdAt), acceptedAt: toIso(item.data().acceptedAt), updatedAt: toIso(item.data().updatedAt),
    })));
  }, onError);
};

export const subscribeEmailSettings = (onData, onError) => {
  if (!firebaseEnabled) return () => {};
  return onSnapshot(doc(db, 'systemSettings', 'email'), snapshot => onData(snapshot.data() || {}), onError);
};
